<template>
  <div>
    <!-- Single price mode -->
    <template v-if="!hasSchemes">
      <div class="sm:flex items-center space-x-3 mb-4">
        <o-field-group :label="$t('general.price')" :info="$t('pages.course.priceTotalInfo')" class="w-full sm:w-1/2">
          <currency-field
            v-model="config.price"
            :label="$t('general.price')"
            :placeholder="$t('pages.course.coursePricePlaceholder')"
            required
          />
        </o-field-group>

        <o-field-group :label="$t('pages.course.deposit')" :info="$t('pages.course.depositInfo')" class="w-full sm:w-1/2">
          <currency-field
            v-model="config.deposit"
            :label="$t('pages.course.deposit')"
          />
        </o-field-group>
      </div>

      <div v-if="!earlyBirdEnabled" class="flex items-center justify-end space-x-2 mt-2">
        <o-tooltip :text="$t('pages.course.earlyBirdTooltip')">
          <o-button flat size="sm" @click="handleEnableEarlyBird">
            {{ $t('pages.course.addEarlyBirdDiscount') }}
          </o-button>
        </o-tooltip>
      </div>

      <div v-else class="border rounded-lg p-3 bg-gray-50 dark:border-gray-800 dark:bg-gray-900 flex items-center space-x-2 relative mt-3">
        <o-button icon="x" class="absolute top-1 right-1" @click="handleDisableEarlyBird" />
        <o-field-group :label="$t('pages.course.earlyBirdPrice')" :info="$t('pages.course.earlyBirdPriceInfo')" class="w-1/2 block">
          <currency-field
            v-model="config.earlyPrice"
            :label="$t('pages.course.earlyBirdPrice')"
            required
          />
        </o-field-group>

        <o-field-group :label="$t('pages.course.earlyBirdOffset')" :info="$t('pages.course.earlyBirdOffsetFirstSession')" class="w-1/2 block">
          <number-field
            v-model="config.earlyOffset"
            :label="$t('pages.course.earlyBirdOffset')"
            required
          />
        </o-field-group>
      </div>

      <div class="flex items-center justify-end mt-3">
        <o-tooltip :text="$t('pages.course.pricingSchemesInfo')">
          <o-button flat size="sm" icon="add" @click="handleEnableSchemes">
            {{ $t('pages.course.addPricingScheme') }}
          </o-button>
        </o-tooltip>
      </div>
    </template>

    <!-- Multiple pricing schemes mode -->
    <template v-else>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-2">
        {{ $t('pages.course.pricingSchemesInfo') }}
      </p>

      <div class="border rounded-lg dark:border-gray-800">
        <div
          v-for="(scheme, index) in config.schemes"
          :key="index"
          class="p-3 border-b dark:border-gray-800 last:border-none"
        >
          <div class="flex items-end space-x-2 mb-2">
            <o-field-group :label="$t('pages.course.schemeName')" class="flex-1 block">
              <text-field
                v-model="scheme.name"
                :label="$t('pages.course.schemeName')"
                :placeholder="$t('pages.course.schemeNamePlaceholder')"
                required
              />
            </o-field-group>

            <o-chip v-if="index === 0" size="sm" flat variant="info" class="mb-2">
              {{ $t('pages.course.defaultScheme') }}
            </o-chip>

            <o-button
              v-if="config.schemes.length > 1"
              icon="x"
              flat
              size="sm"
              class="mb-1"
              @click="handleRemoveScheme(index)"
            />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <o-field-group :label="$t('general.price')" class="block">
              <currency-field
                v-model="scheme.price"
                :label="$t('general.price')"
                :placeholder="$t('pages.course.coursePricePlaceholder')"
                required
              />
            </o-field-group>

            <o-field-group v-if="earlyBirdEnabled" :label="$t('pages.course.earlyBirdPrice')" class="block">
              <currency-field
                v-model="scheme.earlyPrice"
                :label="$t('pages.course.earlyBirdPrice')"
              />
            </o-field-group>

            <o-field-group :label="$t('pages.course.deposit')" class="block">
              <currency-field
                v-model="scheme.deposit"
                :label="$t('pages.course.deposit')"
              />
            </o-field-group>
          </div>
        </div>

        <div class="p-2 border-t dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
          <o-button size="sm" flat icon="add" @click="handleAddScheme">
            {{ $t('pages.course.addAnotherScheme') }}
          </o-button>
        </div>
      </div>

      <!-- Early bird offset is shared across every scheme -->
      <div v-if="!earlyBirdEnabled" class="flex items-center justify-end space-x-2 mt-3">
        <o-tooltip :text="$t('pages.course.earlyBirdTooltip')">
          <o-button flat size="sm" @click="handleEnableEarlyBird">
            {{ $t('pages.course.addEarlyBirdDiscount') }}
          </o-button>
        </o-tooltip>
      </div>

      <div v-else class="border rounded-lg p-3 bg-gray-50 dark:border-gray-800 dark:bg-gray-900 relative mt-3">
        <o-button icon="x" class="absolute top-1 right-1" @click="handleDisableEarlyBird" />
        <o-field-group :label="$t('pages.course.earlyBirdOffset')" :info="$t('pages.course.earlyBirdOffsetFirstSession')" class="w-full sm:w-1/2 block">
          <number-field
            v-model="config.earlyOffset"
            :label="$t('pages.course.earlyBirdOffset')"
            required
          />
        </o-field-group>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {{ $t('pages.course.earlyBirdSchemeInfo') }}
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import { TextField, CurrencyField, NumberField } from '@/components/fields'

export default {
  components: {
    TextField,
    CurrencyField,
    NumberField,
  },
  props: {
    paymentConfig: {
      type: Object,
      required: true,
    },
  },
  computed: {
    // Mutating the prop directly trips vue/no-mutating-props; this alias points
    // at the same object so changes still flow back to the parent's courseRun.
    config () {
      return this.paymentConfig
    },
    hasSchemes () {
      return Array.isArray(this.config.schemes) && this.config.schemes.length > 0
    },
    earlyBirdEnabled () {
      const offset = this.config.earlyOffset
      // In single-price mode an earlyPrice alone also signals early bird is on.
      return (offset !== null && offset !== undefined) || !!this.config.earlyPrice
    },
  },
  watch: {
    // Keep the top-level price/deposit in sync with the default (first) scheme
    // so the rest of the app (run views, completion checks, pickers) keeps
    // working off the existing fields.
    'config.schemes': {
      deep: true,
      handler (schemes) {
        if (Array.isArray(schemes) && schemes.length) {
          const def = schemes[0]
          this.config.price = def.price
          this.config.earlyPrice = def.earlyPrice
          this.config.deposit = def.deposit
        }
      },
    },
  },
  methods: {
    handleEnableEarlyBird () {
      this.config.earlyOffset = 10
      if (!this.hasSchemes) {
        this.config.earlyPrice = 0
      }
    },
    handleDisableEarlyBird () {
      this.config.earlyOffset = null
      this.config.earlyPrice = null
      if (this.hasSchemes) {
        this.config.schemes.forEach((scheme) => {
          scheme.earlyPrice = null
        })
      }
    },
    handleEnableSchemes () {
      const config = this.config
      config.schemes = [
        {
          name: this.$t('pages.course.standardSchemeName'),
          code: 'standard',
          price: config.price ?? null,
          earlyPrice: config.earlyPrice ?? null,
          deposit: config.deposit ?? null,
        },
        {
          name: '',
          price: null,
          earlyPrice: null,
          deposit: null,
        },
      ]
    },
    handleAddScheme () {
      this.config.schemes.push({
        name: '',
        price: null,
        earlyPrice: null,
        deposit: null,
      })
    },
    handleRemoveScheme (index) {
      this.config.schemes.splice(index, 1)

      // A single scheme is just a single price — collapse back to the simple
      // editor and push its values onto the top-level fields.
      if (this.config.schemes.length === 1) {
        const only = this.config.schemes[0]
        this.config.price = only.price
        this.config.earlyPrice = only.earlyPrice
        this.config.deposit = only.deposit
        this.config.schemes = []
      }
    },
  },
}
</script>
